import {UrlUtils} from './api/urls';
import {CookiesManagement} from './cookies_management';
import {searchEventHandler, sphereEventHandler} from './custom_event_utils';
import {SEARCH_EVENT_NAMES as SEARCH_EVENTS, SPHERE_EVENT_NAMES as EVENTS, SPHERE_EVENT_NAMES} from './event-names';
import {MATOMO_EVENT_NAMES} from './metric-events';
import {Metrics} from './metrics';
import Router from './router';
import {
  ANIMATION_PATH_KEY,
  CLUSTER_CAPTION_REGEX,
  CLUSTER_NAME_REGEX,
  DIRECTION,
  PRODUCT_PAGE_ROUTES
} from './shared/constants';
import {
  ActionType,
  AnimateActionData,
  AnimationPath,
  AnimationPathType,
  isAnimateAction,
  isAudioAction,
  isCarouselOverlay,
  isContentOverlayAction,
  isCookiePopupAction,
  isInfoOverlayAction,
  isLinkAction,
  isPlanogramVersionImage,
  isPlanogramVersionProduct,
  isProductOverlayAction,
  isSingleImageAction,
  isSocialLinkAction,
  isVideoOverlayAction,
  SphereItemType
} from 'shared/interfaces/planogram';
import {SphereItem} from './sphere_item';
import {WebUtils} from './utils/web_utils';
import {AnimationOptions, JourneyItems} from './interfaces/planogram.interface';
import {Overlay} from './overlay';
import {L10nUtils} from './utils/l10n_utils';
import {AppUtils} from './utils/app_utils';
import CanvasRenderer from './canvas_renderer';
import {Planogram} from './planogram';
import {SphereItems} from './sphere_items';
import {ProductNavigationData} from './interfaces/product.interface';
import SphereCameraControls from 'shared/renderingEngine/CameraControls';
import {ZoomToAnimation, ZoomToAnimationParams} from 'shared/renderingEngine/animations/ZoomToAnimation';
import {SphereShape} from 'shared/utils/SphereShape';
import {Object3D, Ray, Vector3} from 'three';
import cameraConfig from 'shared/config/CameraConfig';
import {fovForSize} from 'shared/utils/CameraUtils';
import {KeyboardControls} from './controls/keyboard_controls';
import CameraMouseControls, {ClickEvent, MoveEvent} from 'shared/renderingEngine/MouseControls';
import {TapEvent, TouchControls} from 'shared/renderingEngine/TouchControls';
import {throttle} from 'lodash';
import CameraAnimation from 'shared/renderingEngine/animations/CameraAnimation';
import {bubbleHasInputEvent, bubbleInputEvent} from 'shared/utils/three-utils';

const MIN_AUTOPLAY_DURATION = 500;
const CURSOR_UPDATE_LATENCY = 150;

function findObjectItem(object3d: Object3D | undefined): SphereItem | undefined {
  if (object3d === undefined) return undefined;
  let it = object3d;
  while (it.userData.component === undefined && it.parent !== null) it = it.parent;
  return it.userData.component;
}

const INTERACTIVE_EVENTS: Array<keyof WindowEventMap> = ['pointerdown', 'pointerup', 'click'];

export class InputHandler {
  private isClusterSelected: boolean;
  private selectedCluster: string;

  private autoplayStarted: boolean = false;
  private autoplaySpeedTime: number;
  private autoplayTimeoutId: number;
  private repeatCallback: () => void;
  private autoplayAnimation: CameraAnimation;
  private isAnimatingToProduct: boolean;
  private keyboardControls: KeyboardControls;
  private mouseControls: CameraMouseControls;
  private touchControls: TouchControls;

  constructor(
    private canvasRenderer: CanvasRenderer,
    public overlay: Overlay,
    private planogram: Planogram,
    private cameraControls: SphereCameraControls,
    private sphereItems: SphereItems,
    private sphereShape: SphereShape
  ) {
    this.handleClick = this.handleClick.bind(this);
    this.handleTap = this.handleTap.bind(this);
    this.deselectCluster = this.deselectCluster.bind(this);
    this.handlePointerMove = throttle(this.handlePointerMove.bind(this), CURSOR_UPDATE_LATENCY, {trailing: true});

    const canvas = this.canvasRenderer.domElement;
    this.mouseControls = new CameraMouseControls();
    this.mouseControls.init(this.cameraControls, canvas);
    this.mouseControls.addEventListener('click', this.handleClick);
    this.mouseControls.addEventListener('blur', this.deselectCluster);
    this.mouseControls.addEventListener('move', this.handlePointerMove);

    this.touchControls = new TouchControls();
    this.touchControls.init(this.cameraControls, canvas);
    this.touchControls.addEventListener('tap', this.handleTap);

    this.keyboardControls = new KeyboardControls(this.cameraControls, this, this.overlay);
  }

  setProductOverlay(productIdentifier: string, overlayType = PRODUCT_PAGE_ROUTES.GALLERY) {
    const item = this.sphereItems.findSphereItemByIdentifier(productIdentifier);
    if (item && !this.isAnimatingToProduct) {
      this.overlay.showItem(item, overlayType);
    }
  }

  navigateToAutoplayClusters() {
    if (this.autoplayStarted) {
      this.autoplaySpeedTime = Math.max(this.autoplaySpeedTime - 1000, MIN_AUTOPLAY_DURATION);
    } else if (this.selectedCluster) {
      this.startAutoplay();
    } else if (this.planogram.clustersOrder && this.planogram.clustersOrder.length) {
      const clusterId = AppUtils.extractClusterName(this.planogram.clustersOrder[0]);
      Router.navigateToCluster(this.planogram.name, clusterId, {autoplay: true});
    }
  }

  private autoplay() {
    this.repeatCallback = () =>
      (this.autoplayTimeoutId = window.setTimeout(this.autoplay.bind(this), this.autoplaySpeedTime));
    if (this.autoplayTimeoutId && this.autoplayStarted) {
      this.cycleThroughClusters(DIRECTION.RIGHT, true);
    } else {
      const clusterName = this.selectedCluster ? AppUtils.extractClusterName(`cluster-${this.selectedCluster}`) : '';
      Router.navigateToCluster(this.planogram.name, clusterName, {autoplay: true});
    }
  }

  deselectCluster() {
    this.isClusterSelected = false;
  }

  private hoveredObject: Object3D | undefined;

  handlePointerMove(event: MoveEvent) {
    const point = event.point;
    const {meshes} = this.canvasRenderer.getInteractableObjectsAtScreenCoordinate(point.x, point.y);
    const topMesh = meshes[0];
    const item = findObjectItem(topMesh);

    if (topMesh !== this.hoveredObject) {
      if (this.hoveredObject !== undefined) bubbleInputEvent('pointerout', this.hoveredObject);
      if (topMesh !== undefined) bubbleInputEvent('pointerover', topMesh);
      this.hoveredObject = topMesh;
    }

    const interactiveItem = item !== undefined && item.hasInput();
    const interactiveMesh = topMesh !== undefined && bubbleHasInputEvent(INTERACTIVE_EVENTS, topMesh);

    const isInteractive = item === undefined ? interactiveMesh : interactiveItem;

    const canvas = this.canvasRenderer.domElement;
    if (isInteractive) canvas.classList.add('has-cursor-pointer');
    else canvas.classList.remove('has-cursor-pointer');
  }

  handleClick(event: ClickEvent) {
    sphereEventHandler.emit(SPHERE_EVENT_NAMES.CONTROL.CLICK, event);
    this.handlePress(event);
  }

  handleTap(event: TapEvent) {
    sphereEventHandler.emit(SPHERE_EVENT_NAMES.CONTROL.TAP, event);
    this.handlePress(event);
  }

  private handlePress(event: ClickEvent | TapEvent) {
    let isCookieOpened = false;
    try {
      isCookieOpened = CookiesManagement.cookiePopup.isOpen();
    } catch (e) {
      console.error('--- Can not handle response from Cookies popup');
    }

    if (this.overlay.isShowing() || isCookieOpened) {
      return;
    }

    const {meshes, point} = this.canvasRenderer.getInteractableObjectsAtScreenCoordinate(event.point.x, event.point.y);

    const topMesh = meshes[0];
    if (topMesh !== undefined) {
      bubbleInputEvent('pointerdown', topMesh);
      bubbleInputEvent('pointerup', topMesh);
    }

    // TODO: pass pointer events to meshes

    const item = findObjectItem(topMesh);

    if (item === undefined || !item.hasInput()) return;

    sphereEventHandler.emit(EVENTS.CONTROL.CLICK_ITEM, {item});
    sphereEventHandler.emit(EVENTS.HEATMAP.CLICK, {
      x: event.point.x,
      y: event.point.y,
      zoomLevel: this.canvasRenderer.camera.zoomRatio(),
      item
    });

    if (item.action) {
      this.handleAction(item);
    }
  }

  private handleAnimateAction(data: AnimateActionData, planogramName: string, activate: boolean, redirect = false) {
    switch (data.itemType) {
      case SphereItemType.Cluster:
        Router.navigateToCluster(planogramName, AppUtils.extractClusterName(data.clusterLink));
        break;
      case SphereItemType.Product:
        if (!redirect) {
          this.animateCameraToIdentifer(data.productIdentifier, activate);
        } else {
          if (data.productName) {
            Router.navigateToProductIdAndName(
              data.productIdentifier,
              data.productName,
              planogramName,
              !activate && PRODUCT_PAGE_ROUTES.SHOW
            );
          } else {
            Router.navigateToProductId(data.productIdentifier, planogramName, !activate && PRODUCT_PAGE_ROUTES.SHOW);
          }
        }
        break;
      case SphereItemType.AnimationPath:
        if (data.sphereName) {
          const [sphereName, language] = data.sphereName.split('_');
          Router.navigateToPlanogramWithPath(sphereName, data.itemId, {langCode: language});
        } else {
          Router.navigateToPlanogramWithPath(planogramName, data.itemId);
        }
        break;
      case SphereItemType.Image:
      case SphereItemType.Video:
      case SphereItemType.Text:
      case SphereItemType.TextArea:
      case SphereItemType.Shape:
      case SphereItemType.Curve:
        Router.navigateToItem(planogramName, data.itemId, data.itemType);
        break;
      default:
        Router.navigateToPlanogram(planogramName);
    }
  }

  handleAction(item: SphereItem) {
    if (item.action === undefined) return;
    if (isAnimateAction(item.action)) {
      const data = item.action.data;
      if (data.sphereName) {
        const language = data.sphereName.split('_').pop();
        data.sphereName = data.sphereName.replace(`_${language}`, '');
        L10nUtils.selectLanguage(language).then(() =>
          this.handleAnimateAction(data, data.sphereName, data.applyItemsActionInTheEnd, true)
        );
        CookiesManagement.init(this.planogram);
        this.overlay.hide();
      } else {
        this.handleAnimateAction(data, this.planogram.name, data.applyItemsActionInTheEnd);
      }
      Metrics.storeTheEvent(
        this.planogram.name,
        'click',
        `${MATOMO_EVENT_NAMES.WEBGL_CLICK_ANIMATE}_${item.type.toLowerCase()}${WebUtils.getItemName(item, '-')}`
      );
      return;
    }

    if (isLinkAction(item.action) && item.action.type === ActionType.ExternalLink) {
      const url = item.action.data.url;
      if (item.action.data.sameTab === true) WebUtils.redirectToSpecificUrl(url);
      else WebUtils.openLink(item.action.data.url);
      Metrics.storeTheEvent(
        this.planogram.name,
        'click',
        `${MATOMO_EVENT_NAMES.WEBGL_CLICK_LINK}-${item.action.data.url}${WebUtils.getItemName(item)}`
      );
      return;
    }

    if (isProductOverlayAction(item.action)) {
      const product = this.sphereItems.findSphereItemByIdentifier(item.action.data.productIdentifier);
      Router.navigateToProduct(product, PRODUCT_PAGE_ROUTES.GALLERY);
      return;
    }

    if (isAudioAction(item.action)) {
      const url = item.action.data.url;
      sphereEventHandler.emit(EVENTS.AUDIO.PLAY_ACTION, {url});
      return;
    }

    if (isVideoOverlayAction(item.action)) {
      Router.navigateToVideoOverlay(item);
      return;
    }

    if (isLinkAction(item.action) && item.action.type === ActionType.IframeLink) {
      Router.navigateToIframe(item);
      return;
    }

    if (isSocialLinkAction(item.action)) {
      Router.navigateToSocialMedia(item);
      return;
    }

    if (isContentOverlayAction(item.action)) {
      Router.navigateToContentOverlay(item.action.data, item.planogram.name);
      return;
    }

    if (isInfoOverlayAction(item.action)) {
      Router.navigateToInfoOverlay(this.planogram.name, item.action.type, true);
      this.overlay.showItem(item);
      return;
    }

    if (isSingleImageAction(item.action)) {
      let id = 0;
      let name = '';
      if (isPlanogramVersionProduct(item)) {
        id = item.data.picture.id;
        name = item.data.picture.name;
      } else if (isPlanogramVersionImage(item)) {
        id = item.data.id;
        name = item.data.image_name;
      }
      Router.navigateToImage(`${id}`, name.split('.')[0].replace(/\W/g, ''), this.planogram.name, true, {
        silent: true,
        replace: true
      });
      this.overlay.showItem(item);
      return;
    }

    if (isCarouselOverlay(item.action)) {
      Router.navigateToCarouselOverlay(item, this.planogram.name);
      return;
    }

    if (isCookiePopupAction(item.action)) {
      CookiesManagement.reInitCookies();
      return;
    }

    this.overlay.showItem(item);
  }

  private targetPoint = new Vector3();
  private targetNormal = new Vector3();

  private animateTo(item: SphereItem, options: Partial<ZoomToAnimationParams>, callback?: () => void) {
    const [targetPoint, targetNormal] = item.getCenter(this.sphereShape);
    this.targetPoint.copy(targetPoint);
    this.targetNormal.copy(targetNormal);
    const camera = this.cameraControls.sphereCamera;
    const fullItemFOV = fovForSize(
      item.getSize(),
      WebUtils.aspectRatio(),
      camera.initialZPosition() + item.planogram.fixedRadius
    );
    return this.zoomToAnimation(targetPoint, fullItemFOV, options, callback);
  }

  private zoomToAnimation(
    targetPoint: Vector3,
    targetFOV: number,
    options: Partial<ZoomToAnimationParams>,
    callback?: () => void
  ) {
    const camera = this.cameraControls.sphereCamera;
    const startingPoint = camera.getIntersection();
    const settings = this.planogram.animationSettings;
    const animation = new ZoomToAnimation({
      currentFOV: camera.fov,
      targetFOV,
      currentPoint: startingPoint,
      targetPoint,
      endCallback: callback,
      duration: settings.duration,
      transitionType: settings.transition_type,
      panBeforeZoom: settings.pan_before_zoom,
      ...options
    });
    this.cameraControls.addAnimation(animation);
    return animation;
  }

  private animateCameraToCluster(
    clusterIdentification: string,
    delay: number = 0,
    upArrow: boolean = false,
    callback?: () => void
  ): void {
    if (!(this.planogram.clustersOrder && this.planogram.clustersOrder.length)) {
      return;
    }
    if (!callback && this.repeatCallback) {
      callback = this.repeatCallback;
    }
    if (!clusterIdentification) {
      this.selectedCluster = clusterIdentification = this.planogram.clustersOrder[0];
    }
    this.cameraControls.clearAnimations();
    let clusterName: string;
    let animation: CameraAnimation;

    if (clusterIdentification.match(CLUSTER_CAPTION_REGEX)) {
      clusterName = AppUtils.extractClusterFullName(clusterIdentification);
    } else if (clusterIdentification.match(CLUSTER_NAME_REGEX)) {
      clusterName = AppUtils.extractClusterName(clusterIdentification);
    } else {
      clusterName = clusterIdentification;
    }

    const item = this.sphereItems.findClusterByClusterName(clusterName);
    if (item) {
      if (this.selectedCluster === clusterIdentification && this.isClusterSelected && !upArrow) {
        animation = this.zoomOutAnimation();
        this.isClusterSelected = false;
      } else {
        animation = this.animateTo(item, {delay}, callback);
        this.isClusterSelected = true;
      }
      this.selectedCluster = clusterIdentification;

      if (callback && animation) {
        this.autoplayAnimation = animation;
      } else {
        this.resetAutoplay();
      }
    } else {
      console.error('There are no cluster with this name');
    }
  }

  animateToClusterAfterLoad(clusterName: string, delay: number = 0) {
    searchEventHandler.emit(SEARCH_EVENTS.CLOSE_SEARCH);

    const afterAnimationEnd = () => {
      CookiesManagement.isRedirectAnimationProcessing = false;
      CookiesManagement.init(this.planogram);
    };

    const isAutoplay = UrlUtils.getQueryValueFromUrl('autoplay') === 'true';
    if (isAutoplay && !this.autoplayStarted) {
      this.selectedCluster = clusterName;
      this.startAutoplay();
    }

    if (!isAutoplay) {
      this.resetAutoplay();
    }

    CookiesManagement.isRedirectAnimationProcessing = true;
    this.animateCameraToCluster(clusterName, delay, true, afterAnimationEnd);
    if (isAutoplay && this.repeatCallback) {
      this.repeatCallback();
    }
  }

  private animateCameraToItem(identifier: string, callback: () => void, options: AnimationOptions): void {
    const item = this.sphereItems.findSphereItemByIdentifier(identifier);
    if (item) {
      this.cameraControls.clearAnimations();
      this.animateTo(item, options, callback);
    } else {
      console.error('There is no item with this identifier');
    }
  }

  animationPath: AnimationPath;
  journeyItems: JourneyItems = {items: [], productIds: []};
  currentAnimationIndex: number | undefined;

  handleInitAnimationPath(pathIdentifier: string, arrowNavigation = false) {
    const animationPathItem = sessionStorage.getItem(ANIMATION_PATH_KEY);
    const animationPathIndex = new URLSearchParams(window.location.search).get('step');
    const animationPath =
      animationPathItem && !arrowNavigation
        ? JSON.parse(animationPathItem)
        : this.planogram.animation_paths.find(path => path.name === pathIdentifier);

    if (!animationPath || (arrowNavigation && this.animationPath?.name === pathIdentifier)) {
      return;
    }

    this.animationPath = animationPath;
    this.journeyItems.productIds = [];
    this.journeyItems.items = this.animationPath.items.map(journeyItem => {
      const item = this.sphereItems.findSphereItemByIdentifier(journeyItem.itemId) as SphereItem;
      if (item?.action?.type === ActionType.ProductOverlay) {
        this.journeyItems.productIds.push((item.action.data as AnimateActionData)?.productIdentifier);
      }

      return item;
    });
    sessionStorage.removeItem(ANIMATION_PATH_KEY);
    this.currentAnimationIndex = animationPathIndex !== null ? parseInt(animationPathIndex) - 1 : undefined;
    if (!arrowNavigation) {
      if (this.currentAnimationIndex === undefined) this.currentAnimationIndex = -1;
      this.handleNextAnimation(false, this.animationPath.type === AnimationPathType.Journey);
    }
    return this.animationPath;
  }

  handleNextAnimation(reverse?: boolean, initJourney?: boolean) {
    const {items: animations} = this.animationPath;
    if (this.currentAnimationIndex === undefined) {
      if (reverse) this.currentAnimationIndex = animations.length - 1;
      else this.currentAnimationIndex = 1;
    } else {
      this.currentAnimationIndex += reverse ? -1 : 1;
      if (this.animationPath.loop || this.animationPath.type !== AnimationPathType.Custom) {
        this.currentAnimationIndex = (this.currentAnimationIndex + animations.length) % animations.length;
      } else {
        this.currentAnimationIndex = Math.max(0, Math.min(animations.length - 1, this.currentAnimationIndex));
      }
    }

    const animation = animations[this.currentAnimationIndex];
    if (!animation) return;

    const itemId = animation.itemId;
    const differentSphere =
      animation.planogramName !== this.planogram.name || animation.language !== L10nUtils.getCurrentLanguage();
    const item = differentSphere ? undefined : this.sphereItems.findSphereItemByIdentifier(itemId);

    if (differentSphere) {
      sessionStorage.setItem(ANIMATION_PATH_KEY, JSON.stringify(this.animationPath));
      Router.navigateToPlanogramWithPath(
        animation.planogramName,
        this.animationPath.name,
        {
          langCode: animation.language
        },
        this.currentAnimationIndex
      );
    } else if (item === undefined) {
      this.handleNextAnimation(reverse);
    } else {
      const isFinalItem = this.currentAnimationIndex === animations.length - 1;

      if (initJourney && this.animationPath.startWithoutNavigating) {
        this.overlay.showJourneyOverlay(this.journeyItems, this, animation.duration, this.animationPath.autoplay);
        return;
      }

      const urlParams = new URLSearchParams(window.location.search);
      urlParams.set('step', this.currentAnimationIndex.toString());
      window.history.replaceState({}, '', `${window.location.pathname}?${urlParams.toString()}`);

      this.animateCameraToItem(
        itemId,
        () => {
          if (initJourney && !this.animationPath.startWithoutNavigating) {
            this.overlay.showJourneyOverlay(this.journeyItems, this, animation.duration, this.animationPath.autoplay);
          }

          if (this.animationPath.type !== AnimationPathType.Custom) return;
          if (isFinalItem) {
            if (this.animationPath.applyFinalItemsAction) this.handleAction(item);
            if (this.animationPath.loop) this.handleNextAnimation();
          } else this.handleNextAnimation();
        },
        {
          duration: animation.duration,
          transitionType: animation.transitionType,
          panBeforeZoom: animation.panBeforeZoom,
          zoomStopPoint: animation.zoomLevel,
          wait: this.animationPath.type !== AnimationPathType.Custom ? 0 : animation.delay
        }
      );
    }
  }

  redirectToProduct(action: string, productName: string = '') {
    const data = this.parseProductNavigationData(action);
    if (this.planogram.name === data.planogramName) {
      searchEventHandler.emit(SEARCH_EVENTS.SHOW_SPHERE_ELEMENTS);
      this.overlay.hide();
      this.resetNavigation();
      const item = this.sphereItems.findSphereItemByIdentifier(data.productIdentifier);
      if (item) {
        this.animateToProduct(item, () => {
          this.setProductOverlay(item.identifier, data.overlayView);
        });
        Router.navigateToProduct(item, data.overlayView);
      }
    } else {
      const item = {
        planogram: {
          name: data.planogramName
        },
        name: productName,
        identifier: data.productIdentifier
      } as SphereItem;
      Router.navigateToProduct(item, data.overlayView);
    }
  }

  private parseProductNavigationData(action: string): ProductNavigationData {
    const separator = '#';
    const parsedString = action.split(separator).slice(1);
    let overlayView: PRODUCT_PAGE_ROUTES | undefined;
    let planogramName = parsedString.shift();
    if (planogramName === PRODUCT_PAGE_ROUTES.SHOW) {
      overlayView = PRODUCT_PAGE_ROUTES.SHOW;
      planogramName = parsedString.shift();
    }
    const productIdentifier = parsedString.join('-');

    return {
      planogramName,
      productIdentifier,
      overlayView
    };
  }

  private startAutoplay() {
    this.autoplayStarted = true;
    this.autoplaySpeedTime =
      this.planogram.animationSettings.autoplay_delay + this.planogram.animationSettings.duration;
    this.autoplay();
  }

  resetNavigation() {
    if (!window.location.pathname.endsWith(this.planogram.name)) Router.navigateToPlanogram(this.planogram.name);
  }

  resetAutoplay() {
    window.clearTimeout(this.autoplayTimeoutId);
    this.autoplayStarted = false;
    this.repeatCallback = null;
    this.autoplayTimeoutId = null;
    this.cameraControls.removeAnimation(this.autoplayAnimation);
    Router.removeClusterAutoplayState();
  }

  private cycleThroughClusters(direction: DIRECTION, isAutoplay?: boolean): void {
    if (!(this.planogram.clustersOrder && this.planogram.clustersOrder.length)) {
      return;
    }
    const clusterOrderIndex = this.planogram.clustersOrder.indexOf(`cluster-${this.selectedCluster}`);
    const clustersMaxIndex = this.planogram.clustersOrder.length - 1;
    let nextIndex = 0;
    if (clusterOrderIndex >= 0) {
      nextIndex = clusterOrderIndex;
      if (direction === DIRECTION.RIGHT) {
        nextIndex = clusterOrderIndex + 1;
      } else if (direction === DIRECTION.LEFT) {
        nextIndex = clusterOrderIndex - 1;
      }
    } else if (direction === DIRECTION.LEFT) {
      nextIndex = clustersMaxIndex;
    }

    if (nextIndex > clustersMaxIndex) {
      nextIndex = 0;
    } else if (nextIndex < 0) {
      nextIndex = clustersMaxIndex;
    }
    const clusterIdentification = this.planogram.clustersOrder[nextIndex];
    Router.navigateToCluster(this.planogram.name, AppUtils.extractClusterName(clusterIdentification), {
      autoplay: isAutoplay
    });
  }

  animateCameraToClusterByArrows(direction: DIRECTION): void {
    switch (direction) {
      case DIRECTION.UP: {
        if (this.selectedCluster) {
          this.animateCameraToCluster(this.selectedCluster, 0, true);
        } else if (this.planogram.clustersOrder?.length) {
          Router.navigateToCluster(this.planogram.name, AppUtils.extractClusterName(this.planogram.clustersOrder[0]));
        }
        break;
      }
      case DIRECTION.DOWN: {
        if (this.selectedCluster) {
          this.zoomOutAnimation();
        }
        break;
      }
      case DIRECTION.LEFT:
      case DIRECTION.RIGHT: {
        this.cycleThroughClusters(direction);
      }
    }
  }

  private animateCameraToIdentifer(identifier: string, openProductOverlay = true): void {
    const item = this.sphereItems.findSphereItemByIdentifier(identifier);
    if (item) {
      this.animateToProduct(item, () => {
        if (openProductOverlay) {
          this.overlay.showItem(item, PRODUCT_PAGE_ROUTES.GALLERY);
          Router.navigateToProduct(item);
        }
      });
    }
  }

  animateToProductAfterLoad(productIdentifier: string, delay: number = 0, overlayType?: PRODUCT_PAGE_ROUTES) {
    const item = this.sphereItems.findSphereItemByIdentifier(productIdentifier);
    const afterAnimationEnd = () => {
      CookiesManagement.isRedirectAnimationProcessing = false;
      CookiesManagement.init(this.planogram);
      this.overlay.showItem(item, overlayType);
    };
    CookiesManagement.isRedirectAnimationProcessing = true;
    this.animateToProduct(item, afterAnimationEnd, {delay});
  }

  animateToItemAfterLoad(item: SphereItem, delay: number = 0, action?: boolean, overlay?: boolean) {
    const afterAnimationEnd = () => {
      CookiesManagement.isRedirectAnimationProcessing = false;
      CookiesManagement.init(this.planogram);
      if (action) {
        if (overlay) {
          this.overlay.showItem(item);
        } else {
          if (item.action?.type === ActionType.Animate) {
            this.autoplayTimeoutId = window.setTimeout(() => {
              this.handleAction(item);
            }, this.planogram.animationSettings.autoplay_delay);
          } else {
            this.handleAction(item);
          }
        }
      }
    };
    CookiesManagement.isRedirectAnimationProcessing = true;
    this.animateToProduct(item, afterAnimationEnd, {delay});
  }

  animateToImageAfterLoad(itemIdentifier: string, delay: number = 0, action?: boolean) {
    const item = this.sphereItems.findSphereItemByIdentifier(itemIdentifier);
    const afterAnimationEnd = () => {
      CookiesManagement.isRedirectAnimationProcessing = false;
      CookiesManagement.init(this.planogram);
      if (action) {
        if (isCarouselOverlay(item.action)) this.overlay.showContentCarousel(item);
        else this.overlay.showImage(item);
      }
    };
    CookiesManagement.isRedirectAnimationProcessing = true;
    this.animateToProduct(item, afterAnimationEnd, {delay});
  }

  private animateToProduct(
    item?: SphereItem,
    afterAnimationEnd?: Function,
    options?: {duration?: number; delay: number}
  ) {
    if (item) {
      this.cameraControls.clearAnimations();
      this.autoplayAnimation = this.animateTo(item, options, () => {
        if (afterAnimationEnd) {
          afterAnimationEnd();
        }
      });
    }
  }

  isOverlayShowing() {
    return this.overlay.isShowing();
  }

  hideOverlay(withNavigation?: boolean) {
    this.overlay.hide(withNavigation);
  }

  private zoomOutAnimation() {
    if (!this.planogram.clustersOrder) return;
    const camera = this.cameraControls.sphereCamera;
    const clusterHorMid = new Vector3(this.targetPoint.x, 1, this.targetPoint.z);
    const fullItemFOV = camera.clampFOV(cameraConfig.FOV_DEFAULT);
    const cluster = this.sphereShape.castRayFarthestPoint(new Ray(clusterHorMid, this.targetNormal), new Vector3());
    sphereEventHandler.emit(EVENTS.ANIMATE_ZOOM_FOV);
    return this.zoomToAnimation(cluster, fullItemFOV, {});
  }

  dispose() {
    this.mouseControls.removeEventListener('click', this.handleClick);
    this.mouseControls.removeEventListener('blur', this.deselectCluster);
    this.mouseControls.removeEventListener('move', this.handlePointerMove);
    this.mouseControls.dispose();
    this.touchControls.removeEventListener('tap', this.handleTap);
    this.touchControls.dispose();
    this.keyboardControls.dispose();
  }
}
